import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CopyableCell } from '../../components/ui/CopyableCell';
import { CopyableLinkCell } from '../../components/ui/CopyableLinkCell';
import { DriveAssetsBrowser } from '../../components/drive/DriveAssetsBrowser';
import { centsToDisplay } from '../../lib/money';
import { effectiveMetaAdType } from '../../lib/effectiveMetaAdType';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { resolveGroupTag } from '../../lib/groupTag';
import { resolveRowLandingUrl } from '../../lib/landingPageResolution';
import {
  resolveRowGoogleAdLabel,
  resolveRowMetaName,
} from '../../lib/resolveRowTemplates';
import {
  buildRowRenderContext,
  renderRowTemplate,
  type RowRenderContext,
} from '../../lib/rowContent';
import { buildUtmQueryString } from '../../lib/utmQueryString';
import type { Account } from '../../types/account';
import type { AccountCampaign, AccountCampaignInlinePatch } from '../../types/accountCampaign';
import type { AssetLabel, Campaign } from '../../types/campaign';
import type { FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import type { FranchiseProfile } from '../../types/franchiseProfile';
import type { Rollout } from '../../types/rollout';
import type { CurrentUser } from '../../types/user';
import { BuilderCell } from './BuilderCell';
import { DailyBudgetCell } from './DailyBudgetCell';
import { BuildViewField, BuildViewSection } from './BuildViewSection';
import { BuildViewExecutionBlock } from './BuildViewExecutionBlock';
import { BuildViewGoogleContent } from './BuildViewGoogleContent';
import { BuildViewMetaContent } from './BuildViewMetaContent';
import {
  buildDriftFlags,
  dateDriftTitle,
  DriftDot,
} from './BuildViewModificationBadges';
import { isBudgetModified } from '../../lib/dailyBudget';
import {
  addRowLocalAssetLabelStubs,
  addRowOrganicAssetLabelStubs,
  patchRow,
} from './api';

// P2b-3 Build View body — section composition for both Meta and
// Google. This commit lands the top half (Context → Ad Account →
// Resolved Name → Budget & Dates). Subsequent commits extend the
// body downward.
//
// Section order tracks docs/P2b-3_BuildView_Spec.md §2 exactly.
// Platform-conditional bits (resolved name vs label, ad type) gate
// on the platform prop; everything else is shared.

type Props = {
  rollout: Rollout;
  row: AccountCampaign;
  account: Account;
  accounts: Account[];
  locationProfile: FranchiseLocationProfile | null;
  brandFranchiseProfile: FranchiseProfile | null;
  campaign: Campaign | null;
  users: CurrentUser[];
  archived: boolean;
  platform: 'meta' | 'google';
};

export function BuildViewBody(props: Props) {
  const {
    rollout,
    row,
    account,
    locationProfile,
    brandFranchiseProfile,
    campaign,
    archived,
    platform,
  } = props;

  // Single render context per row, reused by every templated section
  // below (Landing URL, UTM, content cells in commits 4-5). Same
  // builder the role grids use, same precedence rules.
  const renderCtx = buildRowRenderContext({
    row,
    campaign,
    account,
    locationProfile,
    brandProfile: brandFranchiseProfile,
    rollout,
  });

  // Round-2-style modification signals consolidated for Build View
  // (commit 6). Per-section flags drive the dot rendering inside each
  // section's badge slot.
  const drift = buildDriftFlags(row, campaign, platform);

  return (
    <div className="space-y-6">
      <ContextSection {...props} />
      <AdAccountSection account={account} platform={platform} />
      <ResolvedNameSection
        row={row}
        account={account}
        locationProfile={locationProfile}
        brandFranchiseProfile={brandFranchiseProfile}
        campaign={campaign}
        rollout={rollout}
        platform={platform}
        modified={drift.resolvedName}
      />
      <BudgetDatesSection
        row={row}
        account={account}
        platform={platform}
        drift={drift}
      />

      {platform === 'meta' ? (
        <BuildViewMetaContent
          rolloutId={rollout.id}
          archived={archived}
          row={row}
          campaign={campaign}
          locationProfile={locationProfile}
          renderCtx={renderCtx}
          drift={drift}
        />
      ) : (
        <BuildViewGoogleContent row={row} renderCtx={renderCtx} drift={drift} />
      )}

      <LandingUrlSection
        row={row}
        brandFranchiseProfile={brandFranchiseProfile}
        locationProfile={locationProfile}
        renderCtx={renderCtx}
        platform={platform}
      />
      {/* Build View polish: Assets moved below Landing URL. Buyers
          working through a build cycle through Content → Landing URL
          first; assets sit alongside UTM as side resources rather
          than headlining the page. Spec §2 renumbered accordingly. */}
      <AssetsSection
        rolloutId={rollout.id}
        row={row}
        campaign={campaign}
        platform={platform}
        modified={drift.assets}
      />
      <UtmConfigSection
        row={row}
        renderCtx={renderCtx}
        platform={platform}
        modified={drift.utm}
      />

      <BuildViewExecutionBlock
        rollout={rollout}
        row={row}
        archived={archived}
        platform={platform}
      />

      {/* Modification badges land in commit 6 (per-section dots, date
          drift, budget warning). Footer Prev/Next + entry icon land
          in commit 7. */}
    </div>
  );
}

// ---------- §2.2 Context ----------

function ContextSection({
  row,
  account,
  accounts,
  locationProfile,
  campaign,
  users,
  archived,
  platform,
  rollout,
}: Props) {
  const accountsById = new Map(accounts.map((a) => [a.id, a]));
  const groupTag = resolveGroupTag(account.id, accountsById);
  const brand = accountsById.get(rollout.brandAccountId) ?? null;
  const am = row.assignedAmId
    ? users.find((u) => u.uid === row.assignedAmId) ?? null
    : null;

  const qc = useQueryClient();
  const patchMut = useMutation({
    mutationFn: (patch: AccountCampaignInlinePatch) =>
      patchRow(rollout.id, row.id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows', 'meta'] });
      qc.invalidateQueries({
        queryKey: ['rollout', rollout.id, 'rows', 'google'],
      });
    },
  });

  const builderField =
    platform === 'meta' ? 'metaBuilderId' : 'googleBuilderId';
  const builderId = row[builderField] ?? null;

  // Effective ad type only renders on the Meta build view.
  const adType =
    platform === 'meta'
      ? effectiveMetaAdType(
          { metaAdType: row.metaAdType ?? null },
          campaign ?? { metaAdCopy: { metaAdType: 'traffic' } },
          locationProfile,
        )
      : null;
  const adTypeLabel =
    adType === 'traffic' ? 'Website Lead' : adType === 'lead_gen' ? 'Meta Lead Gen' : '—';

  return (
    <BuildViewSection id="bv-context" title="Context">
      <dl className="space-y-1">
        <BuildViewField label="Group">
          {groupTag?.name ? (
            groupTag.name
          ) : (
            <span className="text-slate-400">Independent</span>
          )}
        </BuildViewField>
        <BuildViewField label="Brand">
          {brand ? (
            <Link to={`/accounts/${brand.id}`} className="hover:underline">
              {brand.name}
            </Link>
          ) : (
            <span className="text-slate-400">—</span>
          )}
        </BuildViewField>
        <BuildViewField label="AM">
          {am ? (
            formatUserDisplayName(am)
          ) : (
            <span className="text-slate-400">Unassigned</span>
          )}
        </BuildViewField>
        <BuildViewField label="Builder">
          <BuilderCell
            value={builderId}
            users={users}
            disabled={archived}
            ariaLabel={`${platform === 'meta' ? 'Meta' : 'Google'} builder for ${account.name}`}
            onChange={(next) =>
              patchMut.mutate({ [builderField]: next } as AccountCampaignInlinePatch)
            }
          />
        </BuildViewField>
        {platform === 'meta' && (
          <BuildViewField label="Ad type">
            <span className="text-sm">{adTypeLabel}</span>
          </BuildViewField>
        )}
      </dl>
    </BuildViewSection>
  );
}

// ---------- §2.3 Ad Account ----------

function AdAccountSection({
  account,
  platform,
}: {
  account: Account;
  platform: 'meta' | 'google';
}) {
  const url = platform === 'meta' ? account.fbAdAccountUrl : account.googleAdsUrl;
  const label = platform === 'meta' ? 'Meta ad account' : 'Google ad account';
  return (
    <BuildViewSection id="bv-ad-account" title="Ad account">
      <BuildViewField label={label}>
        {/* Round-3 polish Fix 2: compact "Open" label so the cell
            doesn't blow out with a long opaque platform URL. Same
            pattern as the Asset folder cells; the copy icon on hover
            still lets buyers grab the URL. */}
        <CopyableLinkCell
          href={url}
          display={<span>Open</span>}
          ariaLabel={`${label} for ${account.name}`}
        />
      </BuildViewField>
    </BuildViewSection>
  );
}

// ---------- §2.4 Resolved Campaign Name / Ad Label ----------

function ResolvedNameSection({
  row,
  account,
  locationProfile,
  brandFranchiseProfile,
  campaign,
  rollout,
  platform,
  modified,
}: {
  row: AccountCampaign;
  account: Account;
  locationProfile: FranchiseLocationProfile | null;
  brandFranchiseProfile: FranchiseProfile | null;
  campaign: Campaign | null;
  rollout: Rollout;
  platform: 'meta' | 'google';
  modified: boolean;
}) {
  const args = {
    row: {
      campaignDescriptor: row.campaignDescriptor,
      metaCampaignNameTemplate: row.metaCampaignNameTemplate,
      googleAdLabelTemplate: row.googleAdLabelTemplate,
    },
    campaign,
    account,
    locationProfile,
    brandProfile: brandFranchiseProfile,
    rollout,
  };
  const resolved =
    platform === 'meta' ? resolveRowMetaName(args) : resolveRowGoogleAdLabel(args);

  const sectionTitle =
    platform === 'meta' ? 'Resolved Meta campaign name' : 'Resolved Google ad label';
  const ariaLabel =
    platform === 'meta'
      ? `Resolved Meta campaign name for ${account.name}`
      : `Resolved Google ad label for ${account.name}`;

  return (
    <BuildViewSection
      id="bv-resolved-name"
      title={sectionTitle}
      badge={<DriftDot visible={modified} title="Resolved name modified from Campaign" />}
    >
      {resolved.resolved ? (
        <div className="text-base font-mono break-words">
          <CopyableCell
            value={resolved.resolved}
            ariaLabel={ariaLabel}
            display={<span className="text-base font-mono">{resolved.resolved}</span>}
          />
        </div>
      ) : (
        <span className="text-slate-400" title="No naming template set">
          —
        </span>
      )}
    </BuildViewSection>
  );
}

// ---------- §2.5 Budget & Dates ----------

function BudgetDatesSection({
  row,
  account,
  platform,
  drift,
}: {
  row: AccountCampaign;
  account: Account;
  platform: 'meta' | 'google';
  drift: ReturnType<typeof buildDriftFlags>;
}) {
  const monthly =
    platform === 'meta' ? row.metaMonthlyBudget : row.googleMonthlyBudget;
  const increase =
    platform === 'meta' ? row.metaCampaignIncrease : row.googleCampaignIncrease;
  const masterMonthly =
    platform === 'meta'
      ? account.metaMasterMonthlyBudget
      : account.googleMasterMonthlyBudget;
  const dateRange = formatDateRange(row.startDate, row.endDate);

  return (
    <BuildViewSection
      id="bv-budget-dates"
      title="Budget & dates"
      badge={
        <DriftDot visible={drift.dateAny} title={dateDriftTitle(drift)} />
      }
    >
      <dl className="space-y-1">
        <BuildViewField label="Monthly budget">
          {centsToDisplay(monthly, account.currency)}
        </BuildViewField>
        <BuildViewField label="Campaign increase">
          {increase > 0 ? centsToDisplay(increase, account.currency) : '—'}
        </BuildViewField>
        <BuildViewField label="Daily budget">
          {/* DailyBudgetCell already has the amber ⚠ + popover wired
              into its `modified` prop — round 2 Fix 4 surfaced this on
              the role grids; same plumbing here. */}
          <DailyBudgetCell
            row={row}
            platform={platform}
            currency={account.currency}
            modified={isBudgetModified(row, platform)}
            accountMonthlyBudget={masterMonthly}
          />
        </BuildViewField>
        <BuildViewField label="Date range">
          <span className="inline-flex items-center gap-2 font-medium">
            {dateRange}
            <DriftDot visible={drift.dateAny} title={dateDriftTitle(drift)} />
          </span>
        </BuildViewField>
      </dl>
    </BuildViewSection>
  );
}

// ---------- §2.8 Assets (post-polish reorder; was §2.6 pre-reorder) ----------

function AssetsSection({
  rolloutId,
  row,
  campaign,
  platform,
  modified,
}: {
  rolloutId: string;
  row: AccountCampaign;
  campaign: Campaign | null;
  platform: 'meta' | 'google';
  modified: boolean;
}) {
  // OrganicAssets §8 fix: the Build View is a view surface, but it should
  // still seed Local/Organic default labels if it's the first place the
  // folder is browsed (the AM may go straight here). Mirrors the RowEditor
  // view-mode wiring — gated per-folder so Meta/Other never write.
  const qc = useQueryClient();
  const persistStubs = (kind: 'local' | 'organic') => (stubs: AssetLabel[]) => {
    const write =
      kind === 'local'
        ? addRowLocalAssetLabelStubs(rolloutId, row.id, stubs)
        : addRowOrganicAssetLabelStubs(rolloutId, row.id, stubs);
    write
      .then(() => qc.invalidateQueries({ queryKey: ['rollout', rolloutId] }))
      .catch((err) => console.warn(`[label-default] BuildView ${kind} stub-write failed`, err));
  };
  // Build View polish (Fix 1): Asset folders now render as full
  // DriveAssetsBrowser instances in view mode rather than plain
  // "Open + Copy" link cells. Buyers get thumbnails, lightbox,
  // Refresh, Download All, and Open-in-Drive without leaving the
  // page. Folders with no URL set are silently omitted from the
  // section — drift dot in the header still signals when row asset
  // URLs differ from Campaign.
  //
  // Label semantics per folder:
  //   - Meta: row.metaAssetLabels (snapshotted from Campaign at
  //     row creation; Phase 1 has no row-level Meta override).
  //   - Local: row.localAssetLabels (P2c local assets; row-authored
  //     via the row editor's Local Assets browser).
  //   - Google / Other: empty array — no labels schema for these
  //     folders. The browser still surfaces files + lightbox, just
  //     without per-file label overlays.
  const platformFolder =
    platform === 'meta'
      ? row.metaAssetsFolderUrl ?? campaign?.metaAssetsFolderUrl ?? null
      : row.googleAssetsFolderUrl ?? campaign?.googleAssetsFolderUrl ?? null;
  const platformTitle = platform === 'meta' ? 'Meta Assets' : 'Google Assets';
  const platformLabels: AssetLabel[] =
    platform === 'meta' ? row.metaAssetLabels ?? [] : [];
  // Per spec §2.8 the Other Assets URL pulls from the row's snapshot;
  // a separate Campaign-level fallback isn't part of the data model.
  const otherFolder = row.otherAssetsFolderUrl ?? null;
  const localFolder = row.localAssetsUrl ?? null;
  // OrganicAssets spec §7.1: third browser, between Local and Other. Row-
  // scoped labels (row.organicAssetLabels), same view-mode treatment as Local.
  const organicFolder = row.organicAssetsUrl ?? null;

  type AssetSubSection = {
    title: string;
    folderUrl: string;
    labels: AssetLabel[];
    // Local / Organic only — seed default labels + persist stubs from this
    // view surface. Meta (Campaign-snapshotted) + Other (no schema) omit them.
    labelKind?: 'local' | 'organic';
    onStubs?: (stubs: AssetLabel[]) => void;
  };
  const subs: AssetSubSection[] = [];
  if (platformFolder && platformFolder.trim()) {
    subs.push({ title: platformTitle, folderUrl: platformFolder, labels: platformLabels });
  }
  if (localFolder && localFolder.trim()) {
    subs.push({
      title: 'Local Assets',
      folderUrl: localFolder,
      labels: row.localAssetLabels ?? [],
      labelKind: 'local',
      onStubs: persistStubs('local'),
    });
  }
  if (organicFolder && organicFolder.trim()) {
    subs.push({
      title: 'Organic Assets',
      folderUrl: organicFolder,
      labels: row.organicAssetLabels ?? [],
      labelKind: 'organic',
      onStubs: persistStubs('organic'),
    });
  }
  if (otherFolder && otherFolder.trim()) {
    subs.push({ title: 'Other Assets', folderUrl: otherFolder, labels: [] });
  }

  return (
    <BuildViewSection
      id="bv-assets"
      title="Assets"
      badge={<DriftDot visible={modified} title="Asset URLs modified from Campaign" />}
    >
      {/* Meta polish Item 3: read-only Meta Creative Folder Name, above
          the folder browsers. Meta build only — never on Google. */}
      {platform === 'meta' && (
        <div className="mb-4 space-y-1">
          <h3 className="text-sm font-medium text-slate-700">
            Meta Creative Folder Name
          </h3>
          <p className="text-sm text-slate-900">
            {row.metaCreativeFolderName?.trim() ? (
              row.metaCreativeFolderName
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </p>
        </div>
      )}
      {subs.length === 0 ? (
        <span className="text-slate-400 italic text-sm">
          No asset folders configured.
        </span>
      ) : (
        <div className="space-y-6">
          {subs.map((s) => (
            <div key={s.title} className="space-y-2">
              <h3 className="text-sm font-medium text-slate-700">{s.title}</h3>
              <DriveAssetsBrowser
                mode="view"
                title={s.title}
                folderUrl={s.folderUrl}
                value={s.labels}
                // Ad Preview Mockups Stage 1 §2.3: show the Story / Feed
                // badge on every asset card so buyers see at a glance
                // which mockup format an asset will land in on the
                // public preview page. Only meaningful on Meta + Local
                // (Other / Google folders have no label schema to type),
                // but harmless on the empty-label folders since the
                // badge suppresses itself when adType is null/undefined.
                showAdType
                // OrganicAssets §8 fix: Local/Organic seed default labels +
                // persist stubs from this view too (undefined for Meta/Other).
                labelKind={s.labelKind}
                onStubsDetected={s.onStubs}
              />
            </div>
          ))}
        </div>
      )}
    </BuildViewSection>
  );
}

// ---------- §2.7 Landing URL (post-polish reorder; was §2.8 pre-reorder) ----------

function LandingUrlSection({
  row,
  brandFranchiseProfile,
  locationProfile,
  renderCtx,
  platform,
}: {
  row: AccountCampaign;
  brandFranchiseProfile: FranchiseProfile | null;
  locationProfile: FranchiseLocationProfile | null;
  renderCtx: RowRenderContext;
  platform: 'meta' | 'google';
}) {
  // Same resolution chain as the role grids: row override → per-
  // location override → brand template → renderRowTemplate
  // substitution.
  const raw = resolveRowLandingUrl(row, brandFranchiseProfile, locationProfile, platform);
  const url = renderRowTemplate(raw, renderCtx) || null;

  return (
    <BuildViewSection id="bv-landing-url" title="Landing URL">
      {url ? (
        <CopyableLinkCell
          href={url}
          ariaLabel="Landing URL"
        />
      ) : (
        <span className="text-slate-400">—</span>
      )}
    </BuildViewSection>
  );
}

// ---------- §2.9 UTM Config ----------

function UtmConfigSection({
  row,
  renderCtx,
  platform,
  modified,
}: {
  row: AccountCampaign;
  renderCtx: RowRenderContext;
  platform: 'meta' | 'google';
  modified: boolean;
}) {
  const utm = row.utmConfig;
  const queryString = buildUtmQueryString(utm, platform, renderCtx);

  return (
    <BuildViewSection
      id="bv-utm"
      title="UTM config"
      badge={<DriftDot visible={modified} title="UTM config modified from Campaign" />}
    >
      {queryString ? (
        <div className="font-mono text-xs break-all">
          <CopyableCell
            value={queryString}
            ariaLabel="UTM query string"
            display={<span className="font-mono text-xs">{queryString}</span>}
          />
        </div>
      ) : (
        <span className="text-slate-400 italic">No UTM config</span>
      )}
    </BuildViewSection>
  );
}

function formatDateRange(startIso: string | null, endIso: string | null): string {
  const fmt = (iso: string | null) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };
  const a = fmt(startIso);
  const b = fmt(endIso);
  if (!a && !b) return '—';
  if (a && b) return `${a} → ${b}`;
  return a ?? b ?? '—';
}
